import { create } from 'zustand'

export type NotificationCenterItemKind = 'consult' | 'system'

export interface NotificationCenterItem {
  id: string
  kind: NotificationCenterItemKind
  title: string
  description: string
  target?: string
  sessionId?: string
  messageId?: string
  routeRole?: 'user' | 'lawyer'
  createdAt: string
  read: boolean
}

interface NotificationCenterState {
  items: NotificationCenterItem[]
  unreadCount: number
  addNotification: (item: Omit<NotificationCenterItem, 'read'> & { read?: boolean }) => void
  markRead: (id: string) => void
  markAllRead: () => void
  recallConsultNotification: (messageId: string, options?: { description?: string }) => number
  clear: () => void
}

function getUnreadCount(items: NotificationCenterItem[]): number {
  return items.filter((item) => !item.read).length
}

export const useNotificationCenterStore = create<NotificationCenterState>()((set) => ({
  items: [],
  unreadCount: 0,
  addNotification: (item) =>
    set((state) => {
      const nextItem: NotificationCenterItem = {
        ...item,
        read: item.read ?? false,
      }
      const existing = state.items.filter((cur) => cur.id !== nextItem.id)
      const items = [nextItem, ...existing].slice(0, 50)
      return {
        items,
        unreadCount: getUnreadCount(items),
      }
    }),
  markRead: (id) =>
    set((state) => {
      const items = state.items.map((item) => (item.id === id ? { ...item, read: true } : item))
      return {
        items,
        unreadCount: getUnreadCount(items),
      }
    }),
  markAllRead: () =>
    set((state) => {
      const items = state.items.map((item) => ({ ...item, read: true }))
      return {
        items,
        unreadCount: 0,
      }
    }),
  recallConsultNotification: (messageId, options = {}) => {
    const normalizedMessageId = String(messageId).trim()
    if (!normalizedMessageId) return 0

    let recalledUnreadCount = 0
    set((state) => {
      let changed = false
      const items = state.items.map((item) => {
        if (item.kind !== 'consult' || item.messageId !== normalizedMessageId) return item
        changed = true
        if (!item.read) recalledUnreadCount += 1
        return {
          ...item,
          description: options.description ?? '该消息已撤回',
          read: true,
        }
      })

      return changed
        ? {
            items,
            unreadCount: getUnreadCount(items),
          }
        : state
    })

    return recalledUnreadCount
  },
  clear: () => set({ items: [], unreadCount: 0 }),
}))
